import React, { useState } from 'react';
import {
    CalendarIcon as Calendar,
    LocationIcon as MapPin,
    TimeIcon as Clock,
    ArrowLeftIcon as ArrowLeft,
    MailIcon as Mail,
    VerifiedIcon as ShieldCheck,
    UserIcon as User,
    DollarIcon as DollarSign,
    GiftIcon as Gift,
    StarIcon as Star,
    HeartIcon as Heart,
    AlertCircleIcon as AlertCircle,
    InfoIcon as Info,
    CheckIcon as Check,
    EditIcon as Edit2,
    PlusIcon as Plus,
    DeleteIcon as Trash
} from './Icons';
import { EditButton } from './InlineFieldEditor';
import InlineFieldEditor from './InlineFieldEditor';

const EventPreview = ({ event, isEditable = false, onUpdate = () => {} }) => {
    const [editingField, setEditingField] = useState(null); // e.g. 'heroTitle', 'heroSubtitle', 'section-0-title', etc.

    if (!event) return <div className="p-8 text-center">Kein Event-Daten</div>;

    const { pageContent, title, date, startTime, endTime, location, description, isRegistrationEnabled } = event;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isPast = new Date(date) < today;

    const renderSection = (section, index) => {
        const baseClasses = "mb-8 animate-fade-in";

        const iconMap = {
            'Calendar': Calendar, 'MapPin': MapPin, 'Clock': Clock, 'Mail': Mail,
            'ShieldCheck': ShieldCheck, 'User': User, 'DollarSign': DollarSign,
            'Gift': Gift, 'Star': Star, 'Heart': Heart, 'AlertCircle': AlertCircle,
            'Info': Info, 'Check': Check
        };

        switch (section.type) {
            case 'text':
                return (
                    <section key={index} className={baseClasses + " group"}>
                        <div className="flex items-center gap-2 mb-4">
                            {section.title && <h2 className="text-headline-small font-bold text-surface-on">{section.title}</h2>}
                            {isEditable && (
                                <>
                                    <EditButton onClick={() => setEditingField(`section-${index}-title`)} />
                                    <button 
                                        onClick={() => {
                                            const newSections = pageContent.sections.filter((_, i) => i !== index);
                                            onUpdate('pageContent', { ...pageContent, sections: newSections });
                                        }}
                                        className="p-1.5 rounded-full bg-error/10 text-error hover:bg-error hover:text-white transition-all ml-2"
                                    >
                                        <Trash size={14} />
                                    </button>
                                </>
                            )}
                        </div>
                        {isEditable && editingField === `section-${index}-title` && (
                            <div className="mb-4">
                                <InlineFieldEditor 
                                    label="Sektion Titel"
                                    value={section.title || ''}
                                    onSave={(val) => {
                                        const newSections = [...pageContent.sections];
                                        newSections[index] = { ...newSections[index], title: val };
                                        onUpdate('pageContent', { ...pageContent, sections: newSections });
                                        setEditingField(null);
                                    }}
                                    onCancel={() => setEditingField(null)}
                                />
                            </div>
                        )}
                        <div className="relative">
                            <div className="text-body-large text-surface-onVariant whitespace-pre-wrap leading-relaxed">
                                {section.content}
                            </div>
                            {isEditable && (
                                <div className="absolute top-0 right-0">
                                    <EditButton onClick={() => setEditingField(`section-${index}-content`)} />
                                </div>
                            )}
                        </div>
                        {isEditable && editingField === `section-${index}-content` && (
                            <div className="mt-4">
                                <InlineFieldEditor 
                                    label="Sektion Inhalt"
                                    type="textarea"
                                    value={section.content || ''}
                                    onSave={(val) => {
                                        const newSections = [...pageContent.sections];
                                        newSections[index] = { ...newSections[index], content: val };
                                        onUpdate('pageContent', { ...pageContent, sections: newSections });
                                        setEditingField(null);
                                    }}
                                    onCancel={() => setEditingField(null)}
                                />
                            </div>
                        )}
                    </section>
                );
            case 'info-box':
                return (
                    <section key={index} className="mb-8 group relative">
                        <div className="bg-secondary-container text-secondary-onContainer p-6 rounded-2xl flex flex-col items-start">
                            <div className="flex items-center gap-2 w-full justify-between mb-2">
                                {section.title && <h3 className="text-title-large font-bold">{section.title}</h3>}
                                {isEditable && (
                                    <div className="flex gap-2">
                                        <EditButton onClick={() => setEditingField(`section-${index}-all`)} className="!bg-white/20" />
                                        <button onClick={() => onUpdate('pageContent', { ...pageContent, sections: pageContent.sections.filter((_, i) => i !== index) })} className="p-1.5 rounded-full bg-error/10 text-error hover:bg-error hover:text-white transition-all ml-2"><Trash size={14} /></button>
                                    </div>
                                )}
                            </div>
                            <p className="text-body-large whitespace-pre-wrap leading-relaxed">{section.content}</p>
                        </div>
                        {isEditable && editingField === `section-${index}-all` && (
                            <div className="mt-4 space-y-4">
                                <InlineFieldEditor label="Titel" value={section.title || ''} onSave={(val) => { const newS = [...pageContent.sections]; newS[index].title = val; onUpdate('pageContent', { ...pageContent, sections: newS }); }} onCancel={() => {}} />
                                <InlineFieldEditor label="Inhalt" type="textarea" value={section.content || ''} onSave={(val) => { const newS = [...pageContent.sections]; newS[index].content = val; onUpdate('pageContent', { ...pageContent, sections: newS }); setEditingField(null); }} onCancel={() => setEditingField(null)} />
                            </div>
                        )}
                    </section>
                );
            case 'list':
                return (
                    <section key={index} className={baseClasses + " group"}>
                        <div className="flex items-center gap-2 mb-4">
                            {section.title && <h2 className="text-headline-small font-bold text-surface-on">{section.title}</h2>}
                            {isEditable && (
                                <>
                                    <EditButton onClick={() => setEditingField(`section-${index}-title`)} />
                                    <button onClick={() => onUpdate('pageContent', { ...pageContent, sections: pageContent.sections.filter((_, i) => i !== index) })} className="p-1.5 rounded-full bg-error/10 text-error hover:bg-error hover:text-white transition-all ml-2"><Trash size={14} /></button>
                                </>
                            )}
                        </div>
                        {isEditable && editingField === `section-${index}-title` && (
                            <div className="mb-4"><InlineFieldEditor label="Sektion Titel" value={section.title || ''} onSave={(val) => { const newS = [...pageContent.sections]; newS[index].title = val; onUpdate('pageContent', { ...pageContent, sections: newS }); setEditingField(null); }} onCancel={() => setEditingField(null)} /></div>
                        )}
                        <div className="relative">
                            <ul className="space-y-3">
                                {section.items?.map((item, i) => (
                                    <li key={i} className="flex items-start gap-3 text-body-large text-surface-onVariant bg-surface-containerLow p-3 rounded-lg border border-outlineVariant/50">
                                        <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                            {isEditable && <div className="absolute top-0 right-0"><EditButton onClick={() => setEditingField(`section-${index}-items`)} /></div>}
                        </div>
                        {isEditable && editingField === `section-${index}-items` && (
                            <div className="mt-4"><InlineFieldEditor label="Listen-Einträge (Zeilenumbruch für neuen Eintrag)" type="textarea" value={(section.items||[]).join('\n')} onSave={(val) => { const newS = [...pageContent.sections]; newS[index].items = val.split('\n').filter(Boolean); onUpdate('pageContent', { ...pageContent, sections: newS }); setEditingField(null); }} onCancel={() => setEditingField(null)} /></div>
                        )}
                    </section>
                );
            case 'grid':
                return (
                    <section key={index} className={baseClasses + " group"}>
                        <div className="flex items-center gap-2 mb-4">
                            {section.title && <h2 className="text-headline-small font-bold text-surface-on">{section.title}</h2>}
                            {isEditable && (
                                <>
                                    <EditButton onClick={() => setEditingField(`section-${index}-title`)} />
                                    <button onClick={() => onUpdate('pageContent', { ...pageContent, sections: pageContent.sections.filter((_, i) => i !== index) })} className="p-1.5 rounded-full bg-error/10 text-error hover:bg-error hover:text-white transition-all ml-2"><Trash size={14} /></button>
                                </>
                            )}
                        </div>
                        {isEditable && editingField === `section-${index}-title` && (
                            <div className="mb-4"><InlineFieldEditor label="Sektion Titel" value={section.title || ''} onSave={(val) => { const newS = [...pageContent.sections]; newS[index].title = val; onUpdate('pageContent', { ...pageContent, sections: newS }); setEditingField(null); }} onCancel={() => setEditingField(null)} /></div>
                        )}
                        <div className="relative">
                            <div className={`grid gap-4 md:grid-cols-${section.gridColumns || 2}`}>
                                {section.items?.map((item, i) => (
                                    <div key={i} className="bg-surface-containerLow p-5 rounded-2xl border border-outlineVariant shadow-sm">
                                        <p className="text-body-large text-surface-onVariant">{item}</p>
                                    </div>
                                ))}
                            </div>
                            {isEditable && <div className="absolute top-0 right-0"><EditButton onClick={() => setEditingField(`section-${index}-items`)} /></div>}
                        </div>
                        {isEditable && editingField === `section-${index}-items` && (
                            <div className="mt-4"><InlineFieldEditor label="Grid-Einträge (Zeilenumbruch)" type="textarea" value={(section.items||[]).join('\n')} onSave={(val) => { const newS = [...pageContent.sections]; newS[index].items = val.split('\n').filter(Boolean); onUpdate('pageContent', { ...pageContent, sections: newS }); setEditingField(null); }} onCancel={() => setEditingField(null)} /></div>
                        )}
                    </section>
                );
            case 'icon-list':
                return (
                    <section key={index} className={baseClasses + " group"}>
                        <div className="flex items-center gap-2 mb-4">
                            {section.title && <h2 className="text-headline-small font-bold text-surface-on">{section.title}</h2>}
                            {isEditable && (
                                <>
                                    <EditButton onClick={() => setEditingField(`section-${index}-title`)} />
                                    <button onClick={() => onUpdate('pageContent', { ...pageContent, sections: pageContent.sections.filter((_, i) => i !== index) })} className="p-1.5 rounded-full bg-error/10 text-error hover:bg-error hover:text-white transition-all ml-2"><Trash size={14} /></button>
                                </>
                            )}
                        </div>
                        {isEditable && editingField === `section-${index}-title` && (
                            <div className="mb-4"><InlineFieldEditor label="Sektion Titel" value={section.title || ''} onSave={(val) => { const newS = [...pageContent.sections]; newS[index].title = val; onUpdate('pageContent', { ...pageContent, sections: newS }); setEditingField(null); }} onCancel={() => setEditingField(null)} /></div>
                        )}
                        <div className="relative">
                            <ul className="space-y-4">
                                {section.iconItems?.map((item, i) => {
                                    const IconComponent = iconMap[item.icon] || Check;
                                    return (
                                        <li key={i} className="flex items-center gap-4 bg-surface-containerLow p-4 rounded-xl border border-outlineVariant/50">
                                            <div className={`p-3 rounded-full bg-surface-containerHigh ${item.iconColor || 'text-primary'}`}>
                                                <IconComponent sx={{ fontSize: 24 }} />
                                            </div>
                                            <span className="text-body-large font-medium text-surface-on">{item.text}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                            {isEditable && <div className="absolute top-0 right-0"><EditButton onClick={() => setEditingField(`section-${index}-iconItems`)} /></div>}
                        </div>
                        {isEditable && editingField === `section-${index}-iconItems` && (
                            <div className="mt-4"><InlineFieldEditor label="Format: IconName, Text, TailwindColor (eine pro Zeile)" type="textarea" value={(section.iconItems||[]).map(i => `${i.icon}, ${i.text}, ${i.iconColor||''}`).join('\n')} onSave={(val) => { const newS = [...pageContent.sections]; newS[index].iconItems = val.split('\n').filter(Boolean).map(l => { const p = l.split(','); return {icon: p[0]?.trim()||'Check', text: p[1]?.trim()||'', iconColor: p[2]?.trim()||''} }); onUpdate('pageContent', { ...pageContent, sections: newS }); setEditingField(null); }} onCancel={() => setEditingField(null)} /></div>
                        )}
                    </section>
                );
            case 'image':
                const sizeClasses = { 'small': 'max-w-xs', 'medium': 'max-w-md', 'large': 'max-w-2xl', 'full': 'w-full' };
                return (
                    <section key={index} className={`${baseClasses} flex flex-col items-center group`}>
                        <div className="flex items-center gap-2 mb-6 w-full justify-start">
                            {section.title && <h2 className="text-headline-small font-bold text-surface-on">{section.title}</h2>}
                            {isEditable && (
                                <>
                                    <EditButton onClick={() => setEditingField(`section-${index}-title`)} />
                                    <button onClick={() => onUpdate('pageContent', { ...pageContent, sections: pageContent.sections.filter((_, i) => i !== index) })} className="p-1.5 rounded-full bg-error/10 text-error hover:bg-error hover:text-white transition-all ml-2"><Trash size={14} /></button>
                                </>
                            )}
                        </div>
                        {isEditable && editingField === `section-${index}-title` && (
                            <div className="mb-4 w-full"><InlineFieldEditor label="Sektion Titel" value={section.title || ''} onSave={(val) => { const newS = [...pageContent.sections]; newS[index].title = val; onUpdate('pageContent', { ...pageContent, sections: newS }); setEditingField(null); }} onCancel={() => setEditingField(null)} /></div>
                        )}
                        <div className="relative w-full flex justify-center">
                            {section.imageUrl ? (
                                <figure className="relative overflow-hidden rounded-3xl shadow-elevation-2">
                                    <img src={section.imageUrl} alt={section.title || 'Event-Bild'} className={`${sizeClasses[section.imageSize] || 'max-w-md'} object-cover`} />
                                    {section.imageCaption && <figcaption className="absolute bottom-0 left-0 right-0 bg-black/60 text-white p-3 text-sm text-center backdrop-blur-sm">{section.imageCaption}</figcaption>}
                                </figure>
                            ) : (
                                <div className="p-8 border-2 border-dashed border-outline rounded-xl text-center text-surface-onVariant w-full max-w-md">Kein Bild-URL gesetzt</div>
                            )}
                            {isEditable && <div className="absolute top-0 right-0"><EditButton onClick={() => setEditingField(`section-${index}-img`)} /></div>}
                        </div>
                        {isEditable && editingField === `section-${index}-img` && (
                            <div className="mt-4 w-full"><InlineFieldEditor label="Bild-URL" value={section.imageUrl || ''} onSave={(val) => { const newS = [...pageContent.sections]; newS[index].imageUrl = val; onUpdate('pageContent', { ...pageContent, sections: newS }); setEditingField(null); }} onCancel={() => setEditingField(null)} /></div>
                        )}
                    </section>
                );
            default: return null;
        }
    };

    return (
        <div className="bg-surface min-h-full font-sans pb-20 pt-8 md:pt-12">
            <div className="max-w-5xl mx-auto px-4 sm:px-6">

                {/* Header Section (Title & Subtitle) - NO BANNER */}
                <header className="mb-8">
                    {/* Badge / Chip */}
                    {isPast && (
                        <span className="inline-block px-3 py-1 mb-4 rounded-md bg-error text-error-on text-label-large font-bold">
                            Vergangen
                        </span>
                    )}

                    <div className="flex items-center gap-2 mb-4 group">
                        <h1 className="text-display-small md:text-display-medium font-bold text-surface-on break-words">
                            {pageContent?.hero?.title || title}
                        </h1>
                        {isEditable && <EditButton onClick={() => setEditingField('heroTitle')} />}
                    </div>

                    {isEditable && editingField === 'heroTitle' && (
                        <div className="mb-4">
                            <InlineFieldEditor 
                                label="Hero Titel"
                                value={pageContent?.hero?.title || title}
                                onSave={(val) => {
                                    const newPageContent = { ...pageContent, hero: { ...pageContent?.hero, title: val } };
                                    onUpdate('pageContent', newPageContent);
                                    setEditingField(null);
                                }}
                                onCancel={() => setEditingField(null)}
                            />
                        </div>
                    )}

                    <div className="flex items-center gap-2 group">
                        <p className="text-title-medium md:text-headline-small text-surface-onVariant font-medium max-w-3xl leading-relaxed">
                            {pageContent?.hero?.subtitle || "Kein Untertitel"}
                        </p>
                        {isEditable && <EditButton onClick={() => setEditingField('heroSubtitle')} />}
                    </div>

                    {isEditable && editingField === 'heroSubtitle' && (
                        <div className="mt-4">
                            <InlineFieldEditor 
                                label="Hero Untertitel"
                                type="textarea"
                                value={pageContent?.hero?.subtitle || ''}
                                onSave={(val) => {
                                    const newPageContent = { ...pageContent, hero: { ...pageContent?.hero, subtitle: val } };
                                    onUpdate('pageContent', newPageContent);
                                    setEditingField(null);
                                }}
                                onCancel={() => setEditingField(null)}
                            />
                        </div>
                    )}
                </header>

                {/* Hero Image - Contained */}
                <div className="relative group mb-10">
                    {pageContent?.hero?.imageUrl ? (
                        <div className="rounded-3xl overflow-hidden shadow-elevation-1 bg-surface-containerHigh aspect-video max-h-[600px] w-full">
                            <img
                                src={pageContent.hero.imageUrl}
                                alt="Event Hero"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    ) : isEditable && (
                        <div className="rounded-3xl border-2 border-dashed border-outlineVariant p-12 text-center text-surface-onVariant">
                            Kein Hero-Bild vorhanden.
                        </div>
                    )}
                    {isEditable && (
                        <div className="absolute top-4 right-4">
                             <EditButton onClick={() => setEditingField('heroImage')} className="!bg-white/80 !backdrop-blur" />
                        </div>
                    )}
                    {isEditable && editingField === 'heroImage' && (
                        <div className="mt-4 p-4 bg-surface-container rounded-2xl border border-primary/20">
                            <InlineFieldEditor 
                                label="Bild URL"
                                value={pageContent?.hero?.imageUrl || ''}
                                onSave={(val) => {
                                    const newPageContent = { ...pageContent, hero: { ...pageContent?.hero, imageUrl: val } };
                                    onUpdate('pageContent', newPageContent);
                                    setEditingField(null);
                                }}
                                onCancel={() => setEditingField(null)}
                            />
                        </div>
                    )}
                </div>

                {/* Event Meta Card */}
                <div className="md3-card-outlined p-6 md:p-8 !bg-surface-containerLow flex flex-col md:flex-row gap-6 md:gap-12 md:items-center mb-12 rounded-2xl border-outlineVariant">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-primary-container text-primary-onContainer rounded-xl bg-opacity-100">
                            <Calendar sx={{ fontSize: 28 }} />
                        </div>
                        <div>
                            <p className="text-label-medium text-surface-onVariant uppercase tracking-wide">Datum</p>
                            <p className="text-title-medium font-bold text-surface-on">
                                {date ? new Date(date).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: 'long', year: 'numeric' }) : 'TBD'}
                            </p>
                        </div>
                    </div>

                    {(startTime || endTime) && (
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-secondary-container text-secondary-onContainer rounded-xl">
                                <Clock sx={{ fontSize: 28 }} />
                            </div>
                            <div>
                                <p className="text-label-medium text-surface-onVariant uppercase tracking-wide">Uhrzeit</p>
                                <p className="text-title-medium font-bold text-surface-on">
                                    {startTime} {endTime ? `– ${endTime}` : ''} Uhr
                                </p>
                            </div>
                        </div>
                    )}

                    {location && (
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-tertiary-container text-tertiary-onContainer rounded-xl">
                                <MapPin sx={{ fontSize: 28 }} />
                            </div>
                            <div>
                                <p className="text-label-medium text-surface-onVariant uppercase tracking-wide">Ort</p>
                                <p className="text-title-medium font-bold text-surface-on truncate max-w-[200px]">
                                    {location}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Main Content Area */}
                <div className="mt-12">
                    {/* Dynamic Sections */}
                    <div className="space-y-12">
                        {pageContent?.sections?.map((section, index) => renderSection(section, index))}
                    </div>

                    {/* Description Fallback */}
                    {(!pageContent?.sections || pageContent.sections.length === 0) && description && (
                        <div className="mt-8 animate-fade-in">
                            <h3 className="text-headline-small font-bold text-surface-on mb-4">Über dieses Event</h3>
                            <p className="text-body-large text-surface-onVariant leading-relaxed whitespace-pre-line">
                                {description}
                            </p>
                        </div>
                    )}
                </div>

                {/* Registration Call to Action */}
                {isRegistrationEnabled && !isPast && (
                    <div className="mt-16 text-center mb-12">
                        <div className="inline-block p-1 rounded-full bg-gradient-to-r from-primary via-secondary to-tertiary opacity-80">
                            <button disabled className="px-12 py-4 rounded-full bg-surface text-surface-on font-bold text-headline-small shadow-inner cursor-not-allowed">
                                Jetzt anmelden (Vorschau)
                            </button>
                        </div>
                        <p className="mt-4 text-surface-onVariant text-sm">Die Anmeldung ist in der Vorschau deaktiviert.</p>
                    </div>
                )}

                {isEditable && (
                    <div className="mt-12 p-8 border-2 border-dashed border-outline rounded-3xl text-center">
                        <p className="text-surface-onVariant mb-4">Möchtest du weitere Sektionen hinzufügen?</p>
                        <div className="flex flex-wrap gap-2 justify-center">
                            <button onClick={() => onUpdate('pageContent', { ...pageContent, sections: [...(pageContent?.sections || []), { type: 'text', title: 'Neuer Text', content: 'Inhalt hier...' }] })} className="md3-btn-tonal gap-2"><Plus size={16} /> Text</button>
                            <button onClick={() => onUpdate('pageContent', { ...pageContent, sections: [...(pageContent?.sections || []), { type: 'list', title: 'Neue Liste', items: ['Eintrag 1', 'Eintrag 2'] }] })} className="md3-btn-tonal gap-2"><Plus size={16} /> Liste</button>
                            <button onClick={() => onUpdate('pageContent', { ...pageContent, sections: [...(pageContent?.sections || []), { type: 'grid', title: 'Neues Grid', items: ['Kachel 1', 'Kachel 2'], gridColumns: 2 }] })} className="md3-btn-tonal gap-2"><Plus size={16} /> Grid</button>
                            <button onClick={() => onUpdate('pageContent', { ...pageContent, sections: [...(pageContent?.sections || []), { type: 'icon-list', title: 'Neue Icon-Liste', iconItems: [{ icon: 'Check', text: 'Eintrag', iconColor: 'text-primary' }] }] })} className="md3-btn-tonal gap-2"><Plus size={16} /> Icon-Liste</button>
                            <button onClick={() => onUpdate('pageContent', { ...pageContent, sections: [...(pageContent?.sections || []), { type: 'image', title: 'Neues Bild', imageUrl: '', imageSize: 'medium' }] })} className="md3-btn-tonal gap-2"><Plus size={16} /> Bild</button>
                            <button onClick={() => onUpdate('pageContent', { ...pageContent, sections: [...(pageContent?.sections || []), { type: 'info-box', title: 'Neue Info-Box', content: 'Information...' }] })} className="md3-btn-tonal gap-2"><Plus size={16} /> Info-Box</button>
                        </div>
                    </div>
                )}
            </div>

            {/* Back Button Simulation */}
            <div className="fixed bottom-6 left-6 z-40">
                <button disabled className="md3-btn-tonal opacity-50 cursor-not-allowed gap-2 shadow-lg">
                    <ArrowLeft sx={{ fontSize: 18 }} /> Zurück
                </button>
            </div>
        </div>
    );
};

export default EventPreview;
